import { colorArray, colors, accentColor } from '../const';
import { WordCloud, Col, GRID } from '../components/custom';

import {
    BarChart,
    Bar,
    Rectangle,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    ReferenceLine
} from 'recharts';

import { HeatMapGrid } from 'react-grid-heatmap';

function allAnalysisHidden()
{
    const allAnalysis = ['mean', 'top2', 'correlation', 'multiple', 'logistic', 'frequency', 'wordCloud', 'sentiment', 'topicModelling', 'Ngrams', 'emotion', 'Ttest', 'pairedTtest', 'anova', 'chi'];

    const dummy = {};
    allAnalysis.forEach(x => dummy[x] = 'hide');

    return dummy;
}

function truncate(value, limiter)
{
    const limit = Math.floor(45 / limiter);

    const str = String(value);

    if (str.length < limit) return str;

    return `${str.substring(0, limit)}...`;
};

function customLabel(sum, {value, cx, cy, midAngle, outerRadius, payload: incoming})
{
    const
        radius = outerRadius + 20,
        x = cx + radius * Math.cos(-midAngle * Math.PI / 180),
        y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    return (
        <text
            x={x} y={y}
            fontSize={12.5}
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline='center'
            fill={incoming.fill}>
            {`${Math.floor(100 * value / sum)}% (${value})`}
        </text>
    )
};

function generatePlotFromData(payload)
{
    let component;

    const { type, graphData, comment, variables, groups, words } = payload;

    if (type === 'mean' || type === 'top2' || type === 'emotion')
    {
        if (graphData[0].y.length > 1)
        {
            const lines = [];

            for (let i = 0; i < graphData[0].y.length; i++)
            {
                const nameTag = truncate(graphData[0].ynames[i], 3);

                lines.push(
                    <Line
                        key={i}
                        dataKey={(data) => data.y[i]}
                        stroke={colorArray[i]}
                        strokeWidth={2}
                        activeDot={{r: 6, fill: 'white', stroke: '#aaa'}}
                        name={nameTag}
                    />
                )
            }

            component = (
                <ResponsiveContainer height='100%' width='100%' className='chart'>
                    <LineChart data={graphData} layout='horizontal'>
                        <Tooltip/>
                        <CartesianGrid strokeDasharray='5'/>
                        <XAxis dataKey='x' fontSize={15} type='category' tickFormatter={(value) => truncate(value, graphData.length)} padding={{left: 20, right: 20}}/>
                        <YAxis fontSize={15} type='number'/>
                        {lines}
                        <Legend layout='vertical' wrapperStyle={{bottom: 125, right: 40, backgroundColor: 'white', border: `1px solid ${colors.dark80}`, borderRadius: 3, paddingLeft: 7}}/>
                    </LineChart>
                </ResponsiveContainer>
            )
        }
        
        else
        {
            component = (
                <ResponsiveContainer height='100%' width='100%' className='chart'>
                    <BarChart data={graphData} layout='horizontal'>
                        <Tooltip/>
                        <CartesianGrid strokeDasharray='5'/>
                        <XAxis dataKey='x' fontSize={15} type='category' tickFormatter={(value) => truncate(value, graphData.length)} padding={{left: 20, right: 20}}/>
                        <YAxis fontSize={15} type='number'/>
                        <Bar
                            dataKey={(data) => data.y[0]}
                            name='Value'
                            fill={accentColor}
                            barSize={25}
                            className='bar'
                            activeBar={<Rectangle fill='white' stroke={accentColor} strokeWidth={2}/>}>
                            {
                                graphData.map((value, i) =>
                                {
                                    return (
                                        <Cell key={i} fill={colorArray[i % colorArray.length]}/>
                                    )
                                })
                            }
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )
        }
    }

    if (type === 'frequency')
    {
        let sum = 0;

        graphData.forEach(({y}) => sum += y);

        component = (
            <ResponsiveContainer height='100%' width='100%' className='chart'>
                <PieChart data={graphData}>
                    <Tooltip/>
                    <Pie
                        dataKey={(data) => data.y}
                        nameKey={(data) => data.x + ''}
                        innerRadius='55%'
                        outerRadius='70%'
                        paddingAngle={5}
                        label={(data) => customLabel(sum, data)} 
                        labelLine={false}>
                        {
                            graphData.map((value, i) =>
                            {
                                return (
                                    <Cell key={i} fill={colorArray[i % colorArray.length]}/>
                                )
                            })
                        }
                        <text x='50%' y='50%' textAnchor='middle' dominantBaseline='middle' className='descsmall' fontWeight='bold'>{comment}</text>
                    </Pie>
                    <Legend/>
                </PieChart>
            </ResponsiveContainer>
        )
    }

    if (type === 'correlation')
    {
        const cellHeight = `calc(25vw / ${variables.length})`;

        function setCellStyle(x, y, correlationMatrix)
        {
            let r, g, b = 0;

            const
                allValues = correlationMatrix.flat(),
                minCorr = Math.min(...allValues),
                maxCorr = Math.max(...allValues),
                ratio = (correlationMatrix[x][y] - minCorr) / (maxCorr - minCorr),
                clamped = Math.max(0, Math.min(1, ratio));
            
            if (clamped < 0.5)
            {
                r = 255;
                g = Math.round(510 * clamped);
            }

            else
            {
                r = Math.round(510 * (1 - clamped));
                g = 255;
            }

            return {
                backgroundColor: `rgba(${r},${g},${b},0.75)`,
                color: (0.299 * r + 0.587 * g + 0.114 * b) > 140 ? 'black' : 'white',
                fontSize: variables.length > 4 ? `calc(.15 * ${cellHeight})` : `calc(.15 * 30vw / 4)`,
                borderRadius: '.25em'
            }
        }

        if (groups?.length > 0)
        {
            component = [];

            for (let i = 0; i < groups.length; i++)
            {
                component.push(
                    <div className='wrapper' key={i}>
                        <span className='descsmall'>Correlation of {groups[i]} respondents</span>
                        <HeatMapGrid
                            data={graphData[i]}
                            xLabels={variables.map(value => truncate(value, 5))}
                            yLabels={variables.map(value => truncate(value, 5))}
                            cellHeight={cellHeight}
                            xLabelsPos='bottom'
                            square={true}
                            cellRender={(x, y, value) => value.toFixed(2)}
                            cellStyle={(x, y) => setCellStyle(x, y, graphData[i])}
                        />
                    </div>
                );
            }
        }

        else
        {
            component = (
                <HeatMapGrid
                    data={graphData[0]}
                    xLabels={variables.map(value => truncate(value, 5))}
                    yLabels={variables.map(value => truncate(value, 5))}
                    cellHeight={cellHeight}
                    xLabelsPos='bottom'
                    square={true}
                    cellRender={(x, y, value) => value.toFixed(2)}
                    cellStyle={(x, y) => setCellStyle(x, y, graphData[0])}
                />
            )
        }
    }

    if (type === 'wordCloud')
    {
        if (groups?.length > 0)
        {
            component = [];

            for (let i = 0; i < groups.length; i++)
            {
                component.push(
                    <Col className='wrapper' key={i}>
                        <span className='descsmall'>Word Cloud of {groups[i]} respondents</span>
                        <WordCloud
                            words={words[i]}
                            size={[600, 300]}
                            padding={2}
                            fontSizeRange={[10, 60]}
                            rotations={[0]}
                            spiral='rectangular'
                            font='Calibri'
                        />
                    </Col>
                );
            }
        }

        else
        {
            // const { width, height, ref } = useResizeDetector({onResize: ({height, width}) => this.renderCloud([width, height])});

            component = (
                <Col className='wrapper'>
                    <WordCloud
                        words={words[0]}
                        size={[600, 300]}
                        padding={2}
                        fontSizeRange={[10, 60]}
                        rotations={[0]}
                        spiral='rectangular'
                        font='Calibri'
                    />
                    {/* <button onClick={() => state++}>Regenerate Word Cloud</button> */}
                </Col>
            )
        }
    }

    if (type === 'sentiment')
    {
        let sum = 0;
        
        if (groups?.length > 0)
        {
            component = [];

            for (let i = 0; i < groups.length; i++)
            {
                sum = 0;

                graphData[i].forEach(({y}) => sum += y);

                component.push(
                    <Col className='wrapper' key={i} style={{minHeight: '250px'}}>
                        <span className='descsmall'>Sentiment response of {groups[i]} respondents</span>
                        <ResponsiveContainer height='100%' width='100%' className='chart' key={i}>
                            <PieChart data={graphData[0]}>
                                <Tooltip/>
                                <Pie
                                    dataKey={(data) => data.y}
                                    nameKey={(data) => data.x + ''}
                                    label={(data) => customLabel(sum, data)} 
                                    labelLine={false}>
                                    {
                                        graphData.map((value, i) =>
                                        {
                                            return (
                                                <Cell key={i} fill={colorArray[i % colorArray.length]}/>
                                            )
                                        })
                                    }
                                </Pie>
                                <Legend/>
                            </PieChart>
                        </ResponsiveContainer>
                    </Col>
                )
            }
        }

        else
        {
            graphData[0].forEach(({y}) => sum += y);

            component = (
                <ResponsiveContainer height='100%' width='100%' className='chart'>
                    <PieChart data={graphData[0]}>
                        <Tooltip/>
                        <Pie
                            dataKey={(data) => data.y}
                            nameKey={(data) => data.x + ''}
                            label={(data) => customLabel(sum, data)} 
                            labelLine={false}>
                            {
                                graphData.map((value, i) =>
                                {
                                    return (
                                        <Cell key={i} fill={colorArray[i % colorArray.length]}/>
                                    )
                                })
                            }
                        </Pie>
                        <Legend/>
                    </PieChart>
                </ResponsiveContainer>
            )
        }
    }

    if (type === 'topicModelling' || type === 'Ngrams')
    {
        component = [];

        graphData.forEach((data, i) =>
        {
            const fixedGraphData = [];

            for (let i = 0; i < data.words.length; i++)
            {
                fixedGraphData.push({
                    x: data.values[i],
                    y: `${String(data.words[i]).charAt(0).toUpperCase()}${String(data.words[i]).slice(1)}`
                });
            }
            
            component.push(
                <Col className='wrapper' key={i}>
                    <span className='descsmall'>{data.topic} {type === 'Ngrams' ? 'sentiment' : null}</span>
                    <ResponsiveContainer height='100%' width='100%' className='chart'>
                        <BarChart data={fixedGraphData} layout='vertical'>
                            <Tooltip/>
                            <CartesianGrid strokeDasharray='5'/>
                            <XAxis dataKey='x' fontSize={15} type='number'/>
                            <YAxis dataKey='y' fontSize={15} type='category' tickFormatter={(value) => truncate(value, 9)}/>
                            <Bar
                                dataKey='x'
                                name={type === 'Ngrams' ? 'Frequency' : 'Significance'}
                                fill={accentColor}
                                barSize={20}
                                className='bar'
                                activeBar={<Rectangle fill='white' stroke={accentColor} strokeWidth={2}/>}>
                                {
                                    fixedGraphData.map((value, i) =>
                                    {
                                        return (
                                            <Cell key={i} fill={colorArray[i % colorArray.length]}/>
                                        )
                                    })
                                }
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Col>
            )
        });
    }

    if (type === 'logistic')
    {
        const { matrix, stats, table } = payload;

        if (matrix.length > 1)
        {

        }

        else
        {
            const plot = [];

            for (const x in table[0]) { plot.push({x, y: table[0][x]}); }

            component = (
                <Col className='wrapper'>
                    <ResponsiveContainer height='100%' width='100%' className='chart'>
                        <BarChart data={plot} layout='horizontal'>
                            <Tooltip/>
                            <CartesianGrid strokeDasharray='5'/>
                            <XAxis dataKey='x' fontSize={15} type='category' tickFormatter={(value) => truncate(value, plot.length)} padding={{left: 20, right: 20}}/>
                            <YAxis fontSize={15} type='number'/>
                            <Bar
                                dataKey='y'
                                name='Co-efficient'
                                fill={accentColor}
                                barSize={25}
                                className='bar'
                                activeBar={<Rectangle fill='white' stroke={accentColor} strokeWidth={2}/>}>
                                {
                                    plot.map((value, i) =>
                                    {
                                        return (
                                            <Cell key={i} fill={colorArray[i % colorArray.length]}/>
                                        )
                                    })
                                }
                            </Bar>
                            <ReferenceLine y={0} stroke="#000" />
                        </BarChart>
                    </ResponsiveContainer>
                    <span className='desc'>Accuracy: {Math.floor(stats[0].accuracy * 100)}% | Precision: {Math.floor(stats[0].precision * 100)}% | Recall: {Math.floor(stats[0].recall * 100)}%</span>
                    <GRID className='confusionMatrix' style={{gridTemplate: 'repeat(3, auto) / repeat(4, auto)'}}>
                        <span className='descsmall' style={{color: 'white'}}> | </span>
                        <span className='descsmall bold left'>Predicted (NO)</span>
                        <span className='descsmall bold left'>Predicted (YES)</span>
                        <span className='descsmall' style={{color: 'white'}}> | </span>
                        <span className='descsmall bold left'>Actual (NO)</span>
                        <span className='descsmall'>{matrix[0][0][0]}</span>
                        <span className='descsmall'>{matrix[0][0][1]}</span>
                        <span className='descsmall'>Σ {matrix[0][0][0] + matrix[0][0][1]}</span>
                        <span className='descsmall bold left'>Actual (YES)</span>
                        <span className='descsmall'>{matrix[0][1][0]}</span>
                        <span className='descsmall'>{matrix[0][1][1]}</span>
                        <span className='descsmall'>Σ {matrix[0][1][0] + matrix[0][1][1]}</span>
                    </GRID>
                </Col>
            )
        }
    }

    if (type === 'multiple')
    {
        const { results } = payload;

        if (results.length > 1)
        {

        }

        else
        {
            const result = results[0];

            const nameMap = {variables: 'Variables', coeff: 'Coefficient', stderr: 'Std. Error', tstat: 'T-Statistic', pval: 'P-Value', cilow: '95% CI Lower', ciup: '95% CI Upper'};

            const elements = [];

            let i = 0;

            for (const x in result)
            {
                elements.push(<span key={i++} className='bold'>{nameMap[x]}</span>);

                let left = false;

                if (x === 'variables') left = true;

                result[x].forEach(y => elements.push(<span key={i++} className={`${left ? 'left': ''}`}>{left ? truncate(y, 7) : y}</span>));
            }

            component = (
                <Col className='wrapper'>
                    <GRID className='regression' style={{fontSize: '1vw', gridAutoFlow: 'column', gridTemplateRows: `repeat(${result.variables.length + 1}, auto)`}}>
                    {elements}
                    </GRID>
                </Col>
            )
        }
    }

    if (type === 'Ttest')
    {
        const { results, questions } = payload;

        component = [];

        for (let i = 0; i < questions.length; i++)
        {
            component.push(
                <Col className='wrapper'>
                    <span className='descsmall'>{questions[i]}</span>
                    <GRID style={{fontSize: '1vw', gridTemplate: 'repeat(3, auto) / repeat(6, auto)'}}>
                        <span className='bold'>Groups</span>
                        <span className='bold'>Mean</span>
                        <span className='bold'>Count</span>
                        <span className='bold'>Std. Deviation</span>
                        <span className='bold'>T-Value</span>
                        <span className='bold'>P-Value</span>
                        <span className='left'>{results[i].groups[0]}</span>
                        <span>{results[i].mean[0]}</span>
                        <span>{results[i].count[0]}</span>
                        <span>{results[i].stddev[0]}</span>
                        <span>{results[i].tvalue}</span>
                        <span>{results[i].pvalue}</span>
                        <span className='left'>{results[i].groups[1]}</span>
                        <span>{results[i].mean[1]}</span>
                        <span>{results[i].count[1]}</span>
                        <span>{results[i].stddev[1]}</span>
                        <span>{results[i].tvalue}</span>
                        <span>{results[i].pvalue}</span>
                    </GRID>
                </Col>
            );
        }
    }

    if (type === 'anova')
    {
        const { results, questions } = payload;

        component = [];

        for (let i = 0; i < questions.length; i++)
        {
            const fillTable = [];

            for (let j = 0; j < results[i].groups.length; j++)
            {
                fillTable.push(<span className='left'>{results[i].groups[j]}</span>);
                fillTable.push(<span>{results[i].mean[j]}</span>);
                fillTable.push(<span>{results[i].count[j]}</span>);
                fillTable.push(<span>{results[i].stddev[j]}</span>);
                fillTable.push(<span>{results[i].fvalue}</span>);
                fillTable.push(<span>{results[i].pvalue}</span>);
            }

            component.push(
                <Col className='wrapper'>
                    <span className='descsmall'>{questions[i]}</span>
                    <GRID style={{fontSize: '1vw', gridTemplate: `repeat(${results[i].groups.length + 1}, auto) / repeat(6, auto)`}}>
                        <span className='bold'>Groups</span>
                        <span className='bold'>Mean</span>
                        <span className='bold'>Count</span>
                        <span className='bold'>Std. Deviation</span>
                        <span className='bold'>F-Value</span>
                        <span className='bold'>P-Value</span>
                        {fillTable}
                    </GRID>
                </Col>
            );
        }
    }

    if (type === 'chi')
    {
        const items = [];

        const data = payload.results[0];

        ['real', 'expected'].forEach((key) =>
        {
            const temp = [];

            for (let i = 0; i < data.x.length; i++)
            {
                const y = {};

                for (let j = 0; j < data.y.length; j++) y[data.y[j]] = data[key][i][j];

                temp.push({
                   x: data.x[i],
                    ...y
                });
            }

            const bars = [];

            let i = 0;
    
            for (const ykey in temp[0])
            {
                if (ykey === 'x') continue;

                bars.push(<Bar key={Math.random()} dataKey={ykey} fill={colorArray[i]} barSize={25} className='bar' activeBar={<Rectangle fill='white' stroke={colorArray[i]} strokeWidth={2}/>}/>)

                i++;
            }

            items.push(
                <Col className='wrapper' key={Math.random()}>
                    <span className='descsmall'>{key === 'real' ? 'Actual Count' : 'Expected Count'}</span>
                    <ResponsiveContainer height='100%' width='100%' className='chart'>
                        <BarChart data={temp} layout='horizontal'>
                            <Tooltip/>
                            <CartesianGrid strokeDasharray='5'/>
                            <XAxis dataKey='x' fontSize={15} type='category' tickFormatter={(value) => truncate(value, temp.length)} padding={{left: 20, right: 20}}/>
                            <YAxis fontSize={15} type='number'/>
                            {bars}
                            <Legend/>
                        </BarChart>
                    </ResponsiveContainer>
                </Col>
            );
        });

        component = (
            <Col className='wrapper' style={{gap: '5vh'}}>
                <GRID key={Math.random()} style={{gridTemplate: `repeat(4, auto) / repeat(2, auto)`}}>
                    <span className='descsmall bold left'>Chi-Value</span>
                    <span className='descsmall'>{data.chivalue}</span>
                    <span className='descsmall bold left'>P-Value</span>
                    <span className='descsmall'>{data.pvalue}</span>
                    <span className='descsmall bold left'>Alpha Value</span>
                    <span className='descsmall'>{data.alpha}</span>
                    <span className='descsmall bold left'>Degree of Freedom</span>
                    <span className='descsmall'>{data.dof}</span>
                </GRID>
                {items}
            </Col>
        );
    }

    if (type === 'pairedTtest')
    {
        const { results, questions } = payload;

        component = (
            <Col className='wrapper'>
                <span className='descsmall'>{questions[0]} & {questions[1]}</span>
                <GRID style={{gridTemplate: 'repeat(5, auto) / repeat(2, auto)'}}>
                    <span className='descsmall bold'>Mean</span>
                    <span className='descsmall'>{results[0].mean}</span>
                    <span className='descsmall bold'>Std. Deviation</span>
                    <span className='descsmall'>{results[0].stddev}</span>
                    <span className='descsmall bold'>T-Value</span>
                    <span className='descsmall'>{results[0].tvalue}</span>
                    <span className='descsmall bold'>P-Value</span>
                    <span className='descsmall'>{results[0].pvalue}</span>
                </GRID>
            </Col>
        )
    }

    return component;
};

export { generatePlotFromData, allAnalysisHidden };